import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

const RATE_CACHE_KEY = "aevum_fx_rate_v1";
const RATE_TTL_MS = 30 * 60 * 1000;  // 30 minutes

/**
 * useCurrency — provides a SBD⇄AUD (or any pair) toggle plus a live rate.
 * Consumers get: { currency, otherCurrency, rate, toggle, fmt }
 * - `fmt(cents_in_base)` renders the amount in the currently displayed currency
 *   with the correct symbol, applying the FX rate when the display currency
 *   differs from the base currency.
 */
export function useCurrency({ base = "SBD", other = "AUD" } = {}) {
  const [display, setDisplay] = useState(base);
  const [rate, setRate] = useState(null);

  const fetchRate = useCallback(async () => {
    const cache = JSON.parse(localStorage.getItem(RATE_CACHE_KEY) || "null");
    if (cache && cache.base === base && cache.target === other &&
        Date.now() - cache.at < RATE_TTL_MS) {
      setRate(cache.rate);
      return;
    }
    try {
      const r = await api.get(`/money/fx?base=${base}&target=${other}`);
      setRate(r.data.rate);
      localStorage.setItem(RATE_CACHE_KEY, JSON.stringify({
        base: r.data.base, target: r.data.target, rate: r.data.rate, at: Date.now(),
      }));
    } catch {
      setRate(0.18);   // safe fallback
    }
  }, [base, other]);

  useEffect(() => { fetchRate(); }, [fetchRate]);

  const toggle = () => setDisplay((d) => (d === base ? other : base));

  const fmt = (cents_in_base, opts = {}) => {
    if (cents_in_base == null) return "—";
    const symbol = display === "AUD" ? "A$" : display + " ";
    const factor = display === base ? 1 : (rate || 0);
    const value = (cents_in_base / 100) * factor;
    const digits = opts.digits ?? (Math.abs(value) >= 100 ? 0 : 2);
    return `${symbol}${value.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
  };

  return { display, other: display === base ? other : base, rate, toggle, fmt, base };
}
